<div class="cancel-membership-modal">
  <div class="modal-header border-0 pb-0">
    <h4 class="modal-title drop-modal-title" id="modal-basic-title">Cancel Subscription</h4>
    <button type="button"
            class="btn-close"
            aria-label="Close"
            (click)="onKeepMembership()"
            *ngIf="step !== 'success'">
    </button>
  </div>

  <div class="modal-body">
    <!-- Loading -->
    <div *ngIf="step === 'loading'" class="cancel-state text-center py-5">
      <div class="spinner-border text-dark" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="text-muted mt-3 mb-0">Loading cancellation details...</p>
    </div>

    <!-- Error -->
    <div *ngIf="step === 'error'" class="cancel-state">
      <div class="alert alert-danger border-0" role="alert">{{ errorMessage }}</div>
      <button type="button" class="btn btn-dark w-100 mb-2" (click)="loadPreview()">Try Again</button>
      <button type="button" class="btn btn-cancel-secondary w-100" (click)="onKeepMembership()">Close</button>
    </div>

    <!-- Step 1: Consequences -->
    <div *ngIf="step === 'consequences' && preview" class="cancel-step">
      <div *ngIf="showEligibilityBlock" class="eligibility-block mb-3" role="alert">
        <p *ngIf="preview.Eligibility.Code" class="eligibility-code mb-1">{{ preview.Eligibility.Code }}</p>
        <p *ngIf="preview.Eligibility.Message" class="eligibility-message mb-0">{{ preview.Eligibility.Message }}</p>
        <p *ngIf="formattedEarliestEligibleDate" class="eligibility-date mb-0 mt-2">
          Earliest eligible date: {{ formattedEarliestEligibleDate }}
        </p>
      </div>

      <h2 class="pack-title mb-2" *ngIf="packTitle">{{ packTitle }}</h2>

      <p *ngIf="!isTerminal" class="empathy-text mb-3">
        We're so sorry to see you're cancelling your membership.
      </p>

      <h3 *ngIf="isTerminal" class="hero-heading mb-3">{{ heroHeading }}</h3>

      <p *ngIf="!isTerminal && packTitle" class="subhead-text mb-3">
        Cancelling would end your {{ packTitle }} membership.
      </p>

      <div *ngIf="preview.LateCancelNotice" class="late-cancel-banner mb-3" role="alert">
        <i class="bi bi-exclamation-triangle-fill late-cancel-icon" aria-hidden="true"></i>
        <span>{{ preview.LateCancelNotice }}</span>
      </div>

      <div *ngIf="preview.Bullets?.length" class="consequences-card mb-0">
        <p class="consequences-card-label">Here's what would happen</p>
        <ul class="consequences-list list-unstyled mb-0">
          <li *ngFor="let bullet of preview.Bullets">
            <span class="bullet-dot" aria-hidden="true"></span>
            <span>{{ bullet }}</span>
          </li>
        </ul>
      </div>
    </div>

    <!-- Step 2: Reason -->
    <div *ngIf="step === 'reason'" class="cancel-step">
      <button type="button" class="btn btn-link back-link ps-0" (click)="goBackToConsequences()">
        <i class="bi bi-arrow-left me-1"></i> Back
      </button>

      <h3 class="step-title mb-2">Why are you cancelling?</h3>
      <p class="step-subtitle mb-4">Please choose the reason that best fits.</p>

      <div class="reason-list mb-0">
        <label *ngFor="let category of reasonCategories"
               class="reason-option"
               [class.selected]="selectedReasonCategory === category">
          <input class="form-check-input"
                 type="radio"
                 [value]="category"
                 [checked]="selectedReasonCategory === category"
                 (change)="selectReasonCategory(category)">
          <span class="reason-label">{{ category }}</span>
        </label>
      </div>
    </div>

    <!-- Step 3: Confirm -->
    <div *ngIf="step === 'confirm' && preview" class="cancel-step">
      <button type="button" class="btn btn-link back-link ps-0" (click)="goBackToReason()">
        <i class="bi bi-arrow-left me-1"></i> Back
      </button>

      <h3 class="step-title mb-2">Confirm cancellation</h3>
      <p class="confirm-headline mb-3">{{ preview.Headline }}</p>

      <div *ngIf="preview.Bullets?.length" class="consequences-card consequences-card-compact mb-4">
        <ul class="consequences-list list-unstyled mb-0">
          <li *ngFor="let bullet of preview.Bullets">
            <span class="bullet-dot" aria-hidden="true"></span>
            <span>{{ bullet }}</span>
          </li>
        </ul>
      </div>

      <div class="mb-3">
        <div class="d-flex justify-content-between align-items-baseline">
          <label for="reason" class="form-label field-label mb-0">
            Additional details
            <span *ngIf="selectedReasonCategory === 'Other reason'" class="text-danger">*</span>
          </label>
          <span class="details-count"
                [class.text-danger]="isAdditionalDetailsTooLong"
                aria-live="polite">
            {{ additionalDetailsLength }}/{{ additionalDetailsMaxLength }}
          </span>
        </div>
        <textarea id="reason"
                  class="form-control reason-textarea mt-2"
                  rows="4"
                  [(ngModel)]="reasonText"
                  [attr.maxlength]="additionalDetailsMaxLength"
                  [attr.aria-invalid]="isAdditionalDetailsTooLong"
                  aria-describedby="reason-limit"
                  [placeholder]="selectedReasonCategory === 'Other reason'
                    ? 'Tell us more — required for Other reason'
                    : 'Add anything else you\'d like us to know (optional)'">
        </textarea>
        <p id="reason-limit" class="details-hint mb-0 mt-1">
          Maximum {{ additionalDetailsMaxLength }} characters.
        </p>
        <p *ngIf="isAdditionalDetailsTooLong" class="text-danger small mb-0 mt-1" role="alert">
          Additional details must be {{ additionalDetailsMaxLength }} characters or fewer.
        </p>
      </div>

      <div *ngIf="errorMessage" class="alert alert-danger border-0" role="alert">{{ errorMessage }}</div>
    </div>

    <!-- Step 4: Success -->
    <div *ngIf="step === 'success' && preview" class="cancel-step cancel-success text-center">
      <div class="success-icon mb-3" aria-hidden="true">
        <i class="bi bi-check-circle-fill"></i>
      </div>
      <h3 class="step-title mb-2">Cancellation submitted</h3>
      <p class="step-subtitle mb-4">{{ preview.Headline }}</p>

      <div *ngIf="preview.Bullets?.length" class="consequences-card consequences-card-compact text-start mb-0">
        <ul class="consequences-list list-unstyled mb-0">
          <li *ngFor="let bullet of preview.Bullets">
            <span class="bullet-dot" aria-hidden="true"></span>
            <span>{{ bullet }}</span>
          </li>
        </ul>
      </div>
    </div>
  </div>

  <div class="modal-footer border-0" *ngIf="showFooter">
    <ng-container [ngSwitch]="step">
      <ng-container *ngSwitchCase="'consequences'">
        <button type="button"
                class="btn btn-cancel-primary w-100 mb-2"
                [disabled]="!canProceed"
                (click)="goToReason()">
          {{ submitButtonLabel }}
        </button>
        <button type="button" class="btn btn-cancel-secondary w-100" (click)="onKeepMembership()">
          Keep Membership
        </button>
      </ng-container>

      <ng-container *ngSwitchCase="'reason'">
        <button type="button"
                class="btn btn-dark w-100"
                [disabled]="!canContinueFromReason"
                (click)="goToConfirm()">
          Continue
        </button>
      </ng-container>

      <ng-container *ngSwitchCase="'confirm'">
        <button type="button"
                class="btn btn-cancel-primary w-100"
                [disabled]="isLoading || !canSubmit || !canProceed"
                (click)="onSubmit()">
          <span *ngIf="isLoading" class="spinner-border spinner-border-sm me-2"></span>
          {{ isLoading ? 'Submitting...' : submitButtonLabel }}
        </button>
      </ng-container>

      <ng-container *ngSwitchCase="'success'">
        <button type="button" class="btn btn-dark w-100" (click)="onDone()">Done</button>
      </ng-container>
    </ng-container>
  </div>
</div>
